import { useApiTestingLanguage } from './useApiTestingLanguage';
import { RequestTabContent, AuthTabContent } from './TestRequestTabs';
import { HeadersTabContent, BodyTabContent } from './TestPayloadTabs';
import { AssertionsTabContent, ExtractionsTabContent } from './TestValidationTabs';
import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogTrigger
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Plus,
  Settings,
  Key,
  Globe,
  Code,
  Target,
  Download,
  Trash2,
  CheckCircle,
  Folder
} from 'lucide-react';

/**
 * TestFormModal - Create/Edit API Test Modal Component
 * 
 * A comprehensive modal with 6 tabs for configuring API tests:
 * - Request: Name, method, URL, description, environment, project
 * - Auth: Credential selection
 * - Headers: Request headers with autocomplete
 * - Body: Request body with type selection
 * - Assertions: Response validation rules
 * - Extractions: Variable extraction for data chaining
 */
export const TestFormModal = ({
  // Modal state
  open,
  onOpenChange,
  // Form state
  testForm,
  setTestForm,
  editingTest,
  // Data
  environments,
  credentials,
  projects,
  // Actions
  onSave,
  loading
}) => {
  const { t } = useApiTestingLanguage();
  // Reset form when dialog closes
  const handleOpenChange = (isOpen) => {
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Plus className="h-4 w-4 mr-2" />
          {t("Manual Create")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col" data-testid="api-test-modal">
        <DialogHeader className="flex-shrink-0 pb-4 border-b">
          <DialogTitle className="text-xl">{editingTest ? t("Edit API Test") : t("Create API Test")}</DialogTitle>
          <DialogDescription>
            {editingTest ? t("Update your API test configuration") : t("Create a new API endpoint test with request details, authentication, headers, body and assertions")}
          </DialogDescription>
        </DialogHeader>
        
        {/* Main content area with tabs */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0 pt-4">
          <Tabs defaultValue="request" className="flex-1 flex flex-col min-h-0">
            <TabsList className="flex-shrink-0 grid h-auto w-full grid-cols-3 lg:grid-cols-6 mb-4">
              <TabsTrigger value="request" data-testid="tab-request" className="text-xs px-1 sm:px-3 sm:text-sm [&>svg]:hidden sm:[&>svg]:block">
                <Globe className="h-4 w-4 mr-2" />
                {t("Request")}
              </TabsTrigger>
              <TabsTrigger value="auth" data-testid="tab-auth" className="text-xs px-1 sm:px-3 sm:text-sm [&>svg]:hidden sm:[&>svg]:block">
                <Key className="h-4 w-4 mr-2" />
                {t("Auth")}
              </TabsTrigger>
              <TabsTrigger value="headers" data-testid="tab-headers" className="text-xs px-1 sm:px-3 sm:text-sm [&>svg]:hidden sm:[&>svg]:block">
                <Settings className="h-4 w-4 mr-2" />
                {t("Headers")}
                {testForm.headers.filter(h => h.enabled).length > 0 && (
                  <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                    {testForm.headers.filter(h => h.enabled).length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="body" data-testid="tab-body" className="text-xs px-1 sm:px-3 sm:text-sm [&>svg]:hidden sm:[&>svg]:block">
                <Code className="h-4 w-4 mr-2" />
                {t("Body")}
              </TabsTrigger>
              <TabsTrigger value="assertions" data-testid="tab-assertions" className="text-xs px-1 sm:px-3 sm:text-sm [&>svg]:hidden sm:[&>svg]:block">
                <Target className="h-4 w-4 mr-2" />
                {t("Assertions")}
              </TabsTrigger>
              <TabsTrigger value="extractions" data-testid="tab-extractions" className="text-xs px-1 sm:px-3 sm:text-sm [&>svg]:hidden sm:[&>svg]:block">
                <Download className="h-4 w-4 mr-2" />
                {t("Extract")}
                {testForm.extractions && testForm.extractions.length > 0 && (
                  <span className="ml-1 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">
                    {testForm.extractions.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
            
            {/* Tab content - scrollable area */}
            <div className="flex-1 overflow-y-auto min-h-0 pr-2">
              {/* Request Tab */}
              <TabsContent value="request" className="mt-0 space-y-4">
                <RequestTabContent 
                  testForm={testForm}
                  setTestForm={setTestForm}
                  environments={environments}
                  projects={projects}
                />
              </TabsContent>

              {/* Auth Tab */}
              <TabsContent value="auth" className="mt-0 space-y-4">
                <AuthTabContent 
                  testForm={testForm}
                  setTestForm={setTestForm}
                  credentials={credentials}
                />
              </TabsContent>

              {/* Headers Tab */}
              <TabsContent value="headers" className="mt-0 space-y-4">
                <HeadersTabContent 
                  testForm={testForm}
                  setTestForm={setTestForm}
                />
              </TabsContent>

              {/* Body Tab */}
              <TabsContent value="body" className="mt-0 space-y-4">
                <BodyTabContent 
                  testForm={testForm}
                  setTestForm={setTestForm}
                />
              </TabsContent>

              {/* Assertions Tab */}
              <TabsContent value="assertions" className="mt-0 space-y-4">
                <AssertionsTabContent 
                  testForm={testForm}
                  setTestForm={setTestForm}
                />
              </TabsContent>

              {/* Extractions Tab */}
              <TabsContent value="extractions" className="mt-0 space-y-4">
                <ExtractionsTabContent 
                  testForm={testForm}
                  setTestForm={setTestForm}
                />
              </TabsContent>
            </div>
          </Tabs>
        </div>

        {/* Footer with submit button - always visible */}
        <div className="flex-shrink-0 pt-4 mt-4 border-t bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-gray-500">
              {testForm.name && testForm.url ? t("Ready to save") : t("Fill in required fields (Name, URL)")}
            </p>
            <Button 
              onClick={onSave}
              disabled={loading || !testForm.name || !testForm.url}
              className="min-w-[140px]"
              data-testid="btn-save-test"
            >
              {loading ? (editingTest ? t("Updating...") : t("Creating...")) : (editingTest ? t("Update Test") : t("Create Test"))}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TestFormModal;
